import { useEffect, useRef, useState } from 'react';
import { player } from '@/lib/player.ts';
import { useUi } from '@/stores/ui.ts';
import { hasNative, pushBackgroundColor, trackVideoRect } from '@/lib/native.ts';

/** Long enough to catch a real second tap, short enough that pause still
 *  feels immediate. Every phone video app converges on roughly this. */
const DOUBLE_TAP_MS = 300;
const SEEK_STEP_S = 10;

/**
 * The media elements. Mounted ONCE, at the app root, above the router.
 *
 * This is the most important structural decision in the client. Every media
 * app that feels bad mounts its player inside a route: navigate away from
 * /player and the element unmounts and the audio stops. Here the elements
 * live for the lifetime of the app and routes control PRESENTATION only —
 * mini bar or full screen.
 *
 * Browsing the library while radio keeps playing is then free, and that one
 * behaviour is a large share of "feels like a real app".
 */
export function PlayerHost() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (audioRef.current && videoRef.current) {
      player.attach(audioRef.current, videoRef.current);
    }
    // No cleanup: this component never unmounts, and detaching would be the
    // very bug this design exists to prevent.
  }, []);

  /**
   * On the native path the <video> element decodes nothing — it becomes a
   * LAYOUT STAND-IN. It is still positioned by exactly the same CSS as ever,
   * corner PiP in browse and full-bleed in theater, and the native surface is
   * told to follow its rectangle. That is what lets the three-tier
   * presentation model survive a change of decoder without one rule of CSS
   * moving, and why the element is still here rather than conditionally
   * rendered away.
   */
  useEffect(() => {
    if (!hasNative() || !videoRef.current) return;
    document.documentElement.classList.add('native-video');
    pushBackgroundColor();
    return trackVideoRect(videoRef.current);
  }, []);

  // The WebView is transparent so the video shows through, which makes the
  // view behind it the app's background — it has to follow the theme.
  useEffect(() => {
    if (!hasNative()) return;
    const mo = new MutationObserver(() => pushBackgroundColor());
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);

  /*
   * DOUBLE-TAP EITHER SIDE TO SKIP TEN SECONDS — every phone video app
   * teaches this, same as the single tap below teaches pause.
   *
   * A single tap already means pause, and a tap cannot be told apart from
   * the FIRST of a pair of taps until the second one either arrives or
   * doesn't — so pause has to wait a beat to find out, on anything with a
   * timeline to seek along. `player.seekable()` is false for live TV (no
   * timeline, nothing to disambiguate against), and there the old
   * instant, undelayed toggle stands exactly as it was.
   */
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [flash, setFlash] = useState<'back' | 'forward' | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (tapTimer.current) clearTimeout(tapTimer.current);
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const showFlash = (dir: 'back' | 'forward') => {
    // A skip with nothing on screen to say so reads as "nothing happened" —
    // the natural next move is tapping again, harder, into the same
    // silence. THIS is what says the tap landed.
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setFlash(dir);
    flashTimer.current = setTimeout(() => setFlash(null), 500);
  };

  return (
    <>
      <audio ref={audioRef} preload="none" />
      {/* Positioned by CSS depending on route; never conditionally rendered. */}
      {/*
        CLICK THE PICTURE TO PAUSE IT. DOUBLE-CLICK EITHER SIDE TO SKIP.
        
        Every video player on the web does this and its absence is the kind of
        thing nobody reports as a bug — they just quietly find the app
        awkward. In browse the element is a corner thumbnail, where a click
        already means "bring this back", so none of this applies until the
        video is the screen.
        
        Harmless on the native path: there the element decodes nothing and is
        a layout stand-in, but the surface behind it follows its rectangle
        exactly, so a tap lands here and pausing (or seeking) is still the
        right answer.
      */}
      <video
        ref={videoRef}
        id="n8m-video"
        playsInline
        preload="none"
        onClick={(e) => {
          if (useUi.getState().view === 'browse') return;

          if (!player.seekable()) {
            // No timeline, nothing to wait and see about — the old,
            // instant toggle, unchanged.
            player.togglePause();
            return;
          }

          const rect = e.currentTarget.getBoundingClientRect();
          const frac = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;

          if (tapTimer.current) {
            // The second tap of a pair: cancel the pause that was waiting to
            // find out if this would arrive, and act on where THIS one
            // landed instead.
            clearTimeout(tapTimer.current);
            tapTimer.current = null;
            if (frac < 0.4) { player.seekBy(-SEEK_STEP_S); showFlash('back'); }
            else if (frac > 0.6) { player.seekBy(SEEK_STEP_S); showFlash('forward'); }
            else { player.togglePause(); }   // dead centre: no side to prefer
            return;
          }

          tapTimer.current = setTimeout(() => {
            tapTimer.current = null;
            player.togglePause();
          }, DOUBLE_TAP_MS);
        }}
      />

      {flash && (
        <div className={`seek-flash seek-flash-${flash}`} aria-hidden="true">
          <span>{flash === 'back' ? `‹‹ ${SEEK_STEP_S}` : `${SEEK_STEP_S} ››`}</span>
        </div>
      )}
    </>
  );
}
